import {
  checkExhaustive,
  checkExists,
} from 'external/dev_april_corgi+/js/common/asserts';
import {deepEqual} from 'external/dev_april_corgi+/js/common/comparisons';

import {PipeId, NodeSpec} from '../execution/graph';
import {
  PTransformer,
  PipeContext,
  Source,
  walkMaterials,
} from '../execution/pipes';
import {
  ArrayValue,
  BooleanValue,
  ConcentrationValue,
  DataValue,
  PlatedMaterialValue,
  TableType,
  TableValue,
  VolumeValue,
} from '../types/data';

import {LABEL_COLUMN_ID, MixingTransformer, toZero} from './mixing_transformer';
import {makeWaterPlatedMaterial} from './sources';

export const NODE = {
  id: 'node_mix_array',
  label: 'Mix uniformly',
  inputs: [
    {
      id: 'varying',
      label: 'Varying',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
    {
      id: 'uniform',
      label: 'Uniform',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
    {
      id: 'mix',
      label: 'Mixes',
      type: {
        kind: 'table',
        columnLabels: [],
        columnTypes: [],
        defaults: [],
        rowCountFixed: true,
      },
    },
    {
      id: 'negative_control',
      label: 'Negative control',
      type: {
        kind: 'boolean',
      },
    },
    {
      id: 'output_plate',
      label: 'Plate',
      type: {
        kind: 'plate',
        contents: undefined,
      },
    },
    {
      id: 'remainder',
      label: 'Remainder',
      type: {
        kind: 'plated_material',
        material: undefined,
      },
    },
  ],
  outputs: [
    {
      id: 'materials',
      label: 'Materials',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
  ],
  pipe: ({node, overlay}) => {
    const uniform = overlay.padToSources(
      checkExists(node.inputs.get('uniform')),
    );
    const varying = overlay.padToSources(
      checkExists(node.inputs.get('varying')),
    );
    const mix = overlay.singleValuePadSource(
      checkExists(node.inputs.get('mix')),
      'table',
      () => ({
        kind: 'table',
        values: new Map([]),
      }),
    );
    const negativeControl = overlay.singleValuePadSource(
      checkExists(node.inputs.get('negative_control')),
      'boolean',
      () => ({kind: 'boolean', value: true}),
    );
    const plate = overlay.singleValuePadSource(
      checkExists(node.inputs.get('output_plate')),
      'plate',
      context => ({
        kind: 'plate',
        id: context.lab.presetPlates.fourCelsius,
        wells: new Map(),
      }),
    );
    const remainder = overlay.singleValuePadSource(
      checkExists(node.inputs.get('remainder')),
      'plated_material',
      context => makeWaterPlatedMaterial(context),
    );

    const mixes = new FlattenMixToArrayTransformer(
      overlay.nextIdFor(node),
      mix,
      negativeControl,
      uniform as Array<
        Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
      >,
      varying as Array<
        Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
      >,
    );
    const mixing = new MixingTransformer(
      overlay.nextIdFor(node),
      [...uniform, ...varying] as Array<
        Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
      >,
      mixes,
      plate,
      remainder,
    );
    overlay.setPadSource(checkExists(node.outputs.get('materials')), mixing);
  },
} as const satisfies NodeSpec;

const VARYING_COLUMN = 'Varying';

export class FlattenMixToArrayTransformer extends PTransformer<
  DataValue,
  TableValue
> {
  constructor(
    id: PipeId,
    private readonly mix: Source<TableValue>,
    private readonly negativeControl: Source<DataValue>,
    private readonly uniform: Array<
      Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
    >,
    private readonly varying: Array<
      Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
    >,
  ) {
    super(id, [mix, negativeControl, ...uniform, ...varying]);
  }

  async collate(context: PipeContext): Promise<void> {
    const uniform: PlatedMaterialValue[] = [];
    const varying: PlatedMaterialValue[] = [];
    for (const {value, source, reject} of this.values.values()) {
      if (this.uniform.indexOf(source as any) >= 0) {
        walkMaterials(value, reject, v => {
          uniform.push(v);
        });
      } else if (this.varying.indexOf(source as any) >= 0) {
        walkMaterials(value, reject, v => {
          varying.push(v);
        });
      }
    }

    if (varying.length === 0) {
      await this.gatherConstraints({kind: 'table', values: new Map()}, context);
      return;
    }

    const example = varying[0].material.amount;
    let valid = true;
    for (const plated of varying) {
      if (!deepEqual(example, plated.material.amount)) {
        valid = false;
        break;
      }
    }

    const varyingSources = this.varying.map(v =>
      checkExists(this.values.get(v)),
    );
    const mixSource = checkExists(this.values.get(this.mix));
    const negativeControlSource = checkExists(
      this.values.get(this.negativeControl),
    );
    const negativeControl = (negativeControlSource.value as BooleanValue).value;

    if (!valid) {
      await this.gatherConstraints({kind: 'table', values: new Map()}, context);
      for (const {reject} of varyingSources) {
        reject(
          new IncompatibleVaryingsError(
            'Expected all varying materials to have the same concentrations',
          ),
        );
      }
      return;
    }

    const mixValue = checkExists(mixSource.value) as TableValue;
    const [mixesType, mixesValue] = updateTable(mixValue, uniform, example);

    const columns: DataValue[][] = [
      ...varying.map(() => []),
      ...uniform.map(() => []),
    ];
    const varyingAmount = checkExists(mixesValue.values.get(VARYING_COLUMN))[0];
    const zero = toZero(varyingAmount);
    for (let i = 0; i < varying.length + (negativeControl ? 1 : 0); ++i) {
      for (let j = 0; j < varying.length; ++j) {
        columns[j].push(i === j ? varyingAmount : zero);
      }
      for (let j = 0; j < uniform.length; ++j) {
        const label = uniform[j].material.label;
        columns[varying.length + j].push(
          checkExists(mixesValue.values.get(label))[0],
        );
      }
    }

    const providing: TableValue = {
      kind: 'table',
      values: new Map(
        columns.map((c, i) => [
          (i < varying.length ? varying[i] : uniform[i - varying.length])
            .material.label,
          c,
        ]),
      ),
    };
    providing.values.set(
      LABEL_COLUMN_ID,
      Array(varying.length + (negativeControl ? 1 : 0))
        .fill(0)
        .map((_, i) => ({
          kind: 'string',
          value:
            i < varying.length
              ? nextLabel(varying[i].material.label)
              : 'neg_ctrl',
        })),
    );

    try {
      await this.gatherConstraints(providing, context);
    } catch (e: unknown) {
      mixSource.reject(e);
      return;
    }

    await mixSource.resolve({
      type: mixesType,
      value: {
        expected: mixesValue,
        reason: 'May have updated table',
      },
    });
  }
}

function updateTable(
  existing: TableValue,
  uniform: PlatedMaterialValue[],
  varying: ConcentrationValue | VolumeValue,
): [TableType, TableValue] {
  const materials: string[] = [VARYING_COLUMN];
  const initialAmounts: Array<ConcentrationValue | VolumeValue> = [varying];
  for (const plated of uniform) {
    materials.push(plated.material.label);
    initialAmounts.push(plated.material.amount);
  }

  const type: TableType = {
    kind: 'table',
    columnLabels: materials,
    columnTypes: [
      ...initialAmounts.map(a => {
        if (a.kind === 'concentration') {
          return {kind: 'concentration'} as const;
        } else if (a.kind === 'volume') {
          return {
            kind: 'volume',
            minimumNl: 0,
            maximumNl: undefined,
          } as const;
        } else {
          throw checkExhaustive(a);
        }
      }),
    ],
    defaults: initialAmounts,
    rowCountFixed: true,
  };

  const columns = type.columnLabels;
  const rows = 1;

  let willChange = false;
  for (const column of columns) {
    const old = existing?.values?.get(column);
    if (!old || old.length !== rows) {
      willChange = true;
      break;
    }
  }
  if (!willChange && existing?.values.size === columns.length) {
    return [type, existing];
  }

  const table: TableValue = {
    kind: 'table',
    values: new Map(),
  };

  const factories = new Map<string, (i: number) => DataValue>([
    ...materials.map((m, i) => [m, () => initialAmounts[i]] as const),
  ]);
  for (const column of columns) {
    const old = existing.values?.get(column);
    let now;
    if (old) {
      now = old.slice(0, rows);
    } else {
      now = Array(rows);
    }
    table.values.set(column, now);

    const factory = checkExists(factories.get(column));
    for (let i = 0; i < rows; ++i) {
      now[i] ??= toZero(factory(i));
    }
  }

  return [type, table];
}

function nextLabel(label: string): string {
  const existing = label.match(/^(?<root>.+)\.(?<version>\d+)$/);
  if (existing?.groups) {
    const next = Number(existing.groups.version) + 1;
    return `${existing.groups.root}.${next}`;
  } else {
    return `${label}.1`;
  }
}

export class IncompatibleVaryingsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = this.constructor.name;
  }
}
